import React, { useMemo } from 'react';
import { GuestSiteEventDesign } from '../../../layout.types';
import { brannanConfig, useBrannanDesign } from '../../LayoutBrannan.theme';
import { FrameInner, FrameOuter } from '../Frames';
import { Page } from '../Page';
import chroma from 'chroma-js';
import { useColorPaletteProvider } from '../../../ColorPaletteProvider/ColorPaletteProvider';
import { BrannanNavigation, DesignLayoutType } from '@graphql/generated';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { getCustomArtUrl, getIsCustomArtEnabled } from '../../../layout.utils';

const { layerBack, layerMiddle } = brannanConfig.applicatorHtmlProps;

export interface ShellProps {
  eventDesign?: GuestSiteEventDesign;
  eventHandle: string;
  noTranslucency?: boolean;
  isAlohaGuestPage?: boolean;
  isCustomPage?: boolean;
  showTopPadding?: boolean;
  /**
   * When true, bypasses the style applicator loading state.
   * Use this for skeleton components that render before the style applicator
   * script is injected (e.g., RegistryShell during initial load).
   */
  bypassStyleApplicatorLoading?: boolean;
}

export const Shell: React.FC<ShellProps> = ({ eventHandle, eventDesign, isCustomPage, showTopPadding, bypassStyleApplicatorLoading, children }) => {
  const { applicatorConfig, loading: styleApplicatorLoading, CustomStyle } = useBrannanDesign(eventHandle, eventDesign);
  const loading = bypassStyleApplicatorLoading ? false : styleApplicatorLoading;
  const [isSimpleScrollLayoutEnabled] = useIsFeatureEnabled('websiteSimpleScrollLayoutEnabled');
  // Add feature flag here
  const { pageColor, pageFontColor, artBackgroundColor } = useColorPaletteProvider();

  const isSinglePage =
    isSimpleScrollLayoutEnabled && eventDesign?.websiteLayout.layoutType === DesignLayoutType.brannan && eventDesign?.brannanNavigation === BrannanNavigation.singlePage;

  // For some themes (e.g. Rustic Chic) custom background color is not being applied even if enabled
  // This workaround allows to override the default theme color
  const customBackgroundColorConfig = applicatorConfig?.layerBack.customBackgroundColor;
  const customBackgroundColor =
    customBackgroundColorConfig?.isEnabled && customBackgroundColorConfig?.backgroundColor
      ? chroma(customBackgroundColorConfig?.backgroundColor as Array<number>).hex()
      : undefined;

  const customTextColor =
    customBackgroundColorConfig?.isEnabled && customBackgroundColorConfig?.textColor ? chroma(customBackgroundColorConfig?.textColor as Array<number>).hex() : undefined;

  const { isCustomArtEnabled, customArtUrl } = useMemo(
    () => ({
      isCustomArtEnabled: getIsCustomArtEnabled(eventDesign),
      customArtUrl: getCustomArtUrl(eventDesign, DesignLayoutType.brannan),
    }),
    [eventDesign],
  );

  return (
    <FrameOuter
      loading={loading}
      applicatorProp={layerBack.prop}
      customBackgroundColor={customBackgroundColor}
      loadingBackgroundColor={artBackgroundColor}
      customTextColor={customTextColor}
      isCustomPage={isCustomPage}
      customArtUrl={customArtUrl}
      isCustomArtEnabled={isCustomArtEnabled}
    >
      {CustomStyle && <CustomStyle />}
      <FrameInner>
        <Page
          customBackgroundColor={pageColor}
          customTextColor={pageFontColor}
          applicatorProp={layerMiddle.prop}
          showBorder={applicatorConfig?.layerMiddle.border?.isEnabled}
          noTranslucency={!applicatorConfig?.layerMiddle.backgroundTranslucency?.isEnabled}
          isSinglePage={isSinglePage}
          showTopPadding={showTopPadding}
        >
          {children}
        </Page>
      </FrameInner>
    </FrameOuter>
  );
};
